import React, { createContext, useContext, useState, useEffect } from 'react';

export const FullscreenContext = createContext({
    isFullscreen: false,
    setIsFullscreen: () => {},
    toggleFullscreen: () => {}
});

export const FullscreenProvider = ({ children }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        setIsFullscreen(prev => {
            const next = !prev;
            if (next) {
                try {
                    const docEl = document.documentElement;
                    if (docEl.requestFullscreen && !document.fullscreenElement) {
                        docEl.requestFullscreen().catch(() => {});
                    } else if (docEl.webkitRequestFullscreen && !document.webkitFullscreenElement) {
                        docEl.webkitRequestFullscreen().catch(() => {});
                    } else if (docEl.msRequestFullscreen && !document.msFullscreenElement) {
                        docEl.msRequestFullscreen().catch(() => {});
                    }
                } catch (e) {
                    console.error('Fullscreen request error:', e);
                }
            } else {
                try {
                    if (document.exitFullscreen && document.fullscreenElement) {
                        document.exitFullscreen().catch(() => {});
                    } else if (document.webkitExitFullscreen && document.webkitFullscreenElement) {
                        document.webkitExitFullscreen().catch(() => {});
                    } else if (document.msExitFullscreen && document.msFullscreenElement) {
                        document.msExitFullscreen().catch(() => {});
                    }
                } catch (e) {
                    console.error('Fullscreen exit error:', e);
                }
            }
            return next;
        });
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isDocFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
            if (!isDocFullscreen && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullscreen]);

    return (
        <FullscreenContext.Provider value={{ isFullscreen, setIsFullscreen, toggleFullscreen }}>
            {children}
        </FullscreenContext.Provider>
    );
};

export const useFullscreen = () => useContext(FullscreenContext);
